import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { ChatIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FiFlag } from "react-icons/fi";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/Auth";
import {
  addBookClubComment,
  getCommunityBookClub,
  joinCommunityBookClub,
  leaveCommunityBookClub,
  removeBookClubCommentVote,
  reportCommunityContent,
  upvoteBookClubComment,
} from "../../../services/communityApi";
import { trackEvent } from "../../../shared/analytics";
import Spinner from "../../../shared/components/Spinner";
import { formatBookClubSchedule } from "../bookClubSchedule";
import BookClubCountdown from "../components/BookClubCountdown";
import BookClubReading from "../components/BookClubReading";

const reasons = ["scam", "harassment", "spoilers", "inappropriate"];
const reasonLabels = {
  scam: "Scam or spam",
  harassment: "Harassment or bullying",
  spoilers: "Spoilers",
  inappropriate: "Inappropriate content",
};

const formatRelativeTime = (value) => {
  if (!value) {
    return "just now";
  }

  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 1000)
  );
  const units = [
    ["year", 60 * 60 * 24 * 365],
    ["month", 60 * 60 * 24 * 30],
    ["week", 60 * 60 * 24 * 7],
    ["day", 60 * 60 * 24],
    ["hour", 60 * 60],
    ["minute", 60],
  ];

  const unit = units.find(([, seconds]) => elapsedSeconds >= seconds);
  if (!unit) {
    return "just now";
  }

  const [name, seconds] = unit;
  const count = Math.floor(elapsedSeconds / seconds);
  return `${count} ${name}${count === 1 ? "" : "s"} ago`;
};

const AutoGrowTextarea = ({ value, ...props }) => {
  const textareaRef = useRef(null);

  const resize = () => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  useEffect(resize, [value]);

  return (
    <Textarea
      {...props}
      ref={textareaRef}
      value={value}
      rows={1}
      minH="42px"
      resize="none"
      overflow="hidden"
      onInput={resize}
    />
  );
};

const ReportControl = ({ type, id }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { authData } = useAuth();
  const [reason, setReason] = useState(reasons[0]);
  const [open, setOpen] = useState(false);
  const mutation = useMutation(() => reportCommunityContent(type, id, reason), {
    onSuccess: () => {
      setOpen(false);
      toast({
        title: "Report submitted",
        description: "Thanks for helping keep the Book Club welcoming.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    },
  });
  if (!authData) {
    return (
      <Button
        size="xs"
        variant="ghost"
        color="gray.400"
        leftIcon={<FiFlag />}
        onClick={() => navigate("/login")}
      >
        Sign in to report
      </Button>
    );
  }
  return (
    <>
      <Button
        size="xs"
        variant="ghost"
        color="gray.400"
        leftIcon={<FiFlag />}
        onClick={() => setOpen(true)}
      >
        Report
      </Button>
      <Modal isOpen={open} onClose={() => setOpen(false)} isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="gray.700" mx="4">
          <ModalHeader>
            Report {type === "thread" ? "discussion" : "comment"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="gray.300" mb="5">
              Help us keep the Book Club welcoming. Tell us what needs our
              attention and we&apos;ll review it.
            </Text>
            <FormControl>
              <FormLabel color="gray.200" fontSize="sm">
                What is the reason for reporting this?
              </FormLabel>
              <Select
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                bg="gray.800"
                borderColor="gray.600"
              >
                {reasons.map((item) => (
                  <option key={item} value={item}>
                    {reasonLabels[item]}
                  </option>
                ))}
              </Select>
            </FormControl>
            <Text color="gray.500" fontSize="xs" mt="3">
              Reports are private and help us keep conversations safe for
              everyone.
            </Text>
          </ModalBody>
          <ModalFooter gap="3">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              leftIcon={<FiFlag />}
              onClick={() => mutation.mutate()}
              isLoading={mutation.isLoading}
            >
              Submit report
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const CommentItem = ({
  item,
  depth,
  authData,
  clubStatus,
  commentDrafts,
  setCommentDrafts,
  replyOpen,
  setReplyOpen,
  commentMutation,
  voteMutation,
  navigate,
}) => {
  const isReply = depth > 0;
  const draftKey = `reply-${item.id}`;
  const canInteract = clubStatus === "active";
  const isVoting =
    voteMutation.isLoading && voteMutation.variables?.commentId === item.id;

  const handleReply = () => {
    if (!authData) {
      navigate("/login");
      return;
    }
    if (canInteract) {
      setReplyOpen((current) => ({
        ...current,
        [item.id]: !current[item.id],
      }));
    }
  };

  const handleVote = () => {
    if (!authData) {
      navigate("/login");
      return;
    }
    if (canInteract) {
      voteMutation.mutate({ commentId: item.id, voted: item.voted });
    }
  };

  const submitReply = () => {
    commentMutation.mutate({
      threadId: item.threadId,
      body: commentDrafts[draftKey],
      parentId: item.id,
      draftKey,
    });
  };

  return (
    <Box ml="0" mt={isReply ? "3" : "0"} mb={isReply ? "3" : "5"}>
      <Flex align="flex-start" gap="3">
        <Avatar
          size={isReply ? "xs" : "sm"}
          src={item.author?.profilePicture}
          name={item.author?.userHandle || "Redwing community"}
        />
        <Box flex="1" minW="0">
          <Flex align="center" gap="2" wrap="wrap">
            {item.author?.userHandle ? (
              <Link to={`/profile/${item.author.userHandle}`}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ textDecoration: "underline" }}
                >
                  {item.author.userHandle}
                </Text>
              </Link>
            ) : (
              <Text fontSize="sm" fontWeight="bold">
                Redwing community
              </Text>
            )}
            <Text fontSize="xs" color="gray.500">
              · {formatRelativeTime(item.createdAt)}
            </Text>
          </Flex>
          <Text color="gray.300" mt="1" whiteSpace="pre-wrap">
            {item.body}
          </Text>
          <Flex align="center" gap="1" mt="2" wrap="wrap">
            <Flex align="center">
              <IconButton
                aria-label={item.voted ? "Remove upvote" : "Upvote"}
                icon={<ChevronUpIcon boxSize="5" />}
                size="xs"
                variant="ghost"
                color={item.voted ? "red.300" : "gray.400"}
                onClick={handleVote}
                isLoading={isVoting}
                isDisabled={!canInteract}
              />
              <Text
                minW="6"
                color={item.voted ? "red.300" : "gray.400"}
                fontSize="xs"
                fontWeight="bold"
                textAlign="center"
              >
                {item.voteCount ?? item.upvotes ?? 0}
              </Text>
              <IconButton
                aria-label="Downvote (not available)"
                icon={<ChevronDownIcon boxSize="5" />}
                size="xs"
                variant="ghost"
                color="gray.600"
                isDisabled
              />
            </Flex>
            {!isReply && (
              <Button
                size="xs"
                variant="ghost"
                color="gray.400"
                leftIcon={<ChatIcon />}
                onClick={handleReply}
                isDisabled={!canInteract && !!authData}
              >
                Reply
              </Button>
            )}
            <ReportControl type="comment" id={item.id} />
          </Flex>
          {!isReply && replyOpen[item.id] && (
            <Flex gap="2" mt="3">
              <AutoGrowTextarea
                size="sm"
                value={commentDrafts[draftKey] || ""}
                onChange={(event) =>
                  setCommentDrafts((current) => ({
                    ...current,
                    [draftKey]: event.target.value,
                  }))
                }
                placeholder="Reply to this comment"
              />
              <Button
                alignSelf="end"
                colorScheme="red"
                onClick={submitReply}
                isLoading={commentMutation.isLoading}
                isDisabled={!commentDrafts[draftKey]?.trim()}
              >
                Reply
              </Button>
            </Flex>
          )}
        </Box>
      </Flex>
      {!isReply && item.replies?.length > 0 && (
        <Box
          position="relative"
          ml={{ base: "4", md: "4" }}
          pl={{ base: "4", md: "4" }}
          borderLeftWidth="1px"
          borderColor="gray.600"
          _before={{
            content: '""',
            position: "absolute",
            top: "0",
            left: "0",
            width: "4",
            borderTopWidth: "1px",
            borderColor: "gray.600",
          }}
        >
          {item.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              item={{ ...reply, threadId: item.threadId }}
              depth={1}
              authData={authData}
              clubStatus={clubStatus}
              commentDrafts={commentDrafts}
              setCommentDrafts={setCommentDrafts}
              replyOpen={replyOpen}
              setReplyOpen={setReplyOpen}
              commentMutation={commentMutation}
              voteMutation={voteMutation}
              navigate={navigate}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const BookClub = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery("communityBookClub", getCommunityBookClub);
  const club = query.data?.data;
  const leaveCancelRef = useRef();
  const [isLeaveOpen, setIsLeaveOpen] = useState(false);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [replyOpen, setReplyOpen] = useState({});
  const joinMutation = useMutation(joinCommunityBookClub, {
    onSuccess: () => {
      trackEvent("book_club_joined", {
        book_club_id: club?.id,
        source: "book_club_page",
      });
      queryClient.invalidateQueries("communityBookClub");
    },
  });
  const leaveMutation = useMutation(leaveCommunityBookClub, {
    onSuccess: () => {
      trackEvent("book_club_left", { book_club_id: club?.id });
      setIsLeaveOpen(false);
      queryClient.invalidateQueries("communityBookClub");
      queryClient.invalidateQueries("communityHub");
      queryClient.invalidateQueries("readings");
      queryClient.invalidateQueries("paywall");
      if (club?.readingId) {
        queryClient.invalidateQueries(["reading", club.readingId]);
        queryClient.invalidateQueries(["readingOrder", club.readingId]);
      }
      toast({
        title: "You left the Book Club.",
        description: "The reading order is still available in your Reading.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      navigate("/community");
    },
    onError: (error) => {
      toast({
        title: "Could not leave the Book Club.",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });
  const commentMutation = useMutation(
    ({ threadId, body, parentId }) =>
      addBookClubComment(threadId, body, parentId),
    {
      onSuccess: (_, variables) => {
        trackEvent("book_club_comment_posted", {
          book_club_id: club?.id,
          thread_id: variables.threadId,
          is_reply: Boolean(variables.parentId),
        });
        setCommentDrafts((current) => {
          const next = { ...current };
          delete next[variables.draftKey];
          return next;
        });
        if (variables.parentId) {
          setReplyOpen((current) => ({
            ...current,
            [variables.parentId]: false,
          }));
        }
        queryClient.invalidateQueries("communityBookClub");
      },
    }
  );
  const voteMutation = useMutation(
    ({ commentId, voted }) =>
      voted
        ? removeBookClubCommentVote(commentId)
        : upvoteBookClubComment(commentId),
    {
      onSuccess: (_, variables) => {
        trackEvent("book_club_comment_voted", {
          book_club_id: club?.id,
          comment_id: variables.commentId,
          voted: !variables.voted,
        });
        queryClient.invalidateQueries("communityBookClub");
      },
    }
  );

  if (query.isLoading) return <Spinner />;
  if (!club)
    return (
      <Container py="8">
        <Heading size="md">No Book Club yet</Heading>
        <Text mt="2" color="gray.400">
          Check back soon for the next community read.
        </Text>
      </Container>
    );

  return (
    <Container maxW="container.xl" py="8">
      <Flex mb="4" align="center" justify="space-between" gap="3">
        <Button variant="ghost" onClick={() => navigate("/community")}>
          ← The Panel
        </Button>
        {club.joined ? (
          <Button
            color="white"
            borderColor="whiteAlpha.500"
            variant="outline"
            size="sm"
            _hover={{ bg: "whiteAlpha.100", borderColor: "white" }}
            onClick={() => setIsLeaveOpen(true)}
          >
            Leave Book Club
          </Button>
        ) : null}
      </Flex>
      <Box bg="gray.700" borderRadius="lg" overflow="hidden">
        <Flex direction={{ base: "column", md: "row" }}>
          {club.cover && (
            <Box
              w={{ base: "100%", md: "280px" }}
              minH="220px"
              bgImage={`url(${club.cover})`}
              bgSize="cover"
              bgPosition="center"
            />
          )}
          <Box p={{ base: "5", md: "8" }} flex="1">
            <Text fontSize="sm" color="red.300" fontWeight="bold">
              BOOK CLUB · {club.status.toUpperCase()}
            </Text>
            <Heading mt="2">{club.title}</Heading>
            <Text color="gray.300" mt="4">
              {club.description}
            </Text>
            <Text color="gray.400" mt="3" fontSize="sm">
              {formatBookClubSchedule(club.startsAt, club.endsAt)} ·{" "}
              {club.memberCount} members
            </Text>
            {!club.joined && club.status !== "archived" ? (
              <Flex mt="5">
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={
                    authData
                      ? () => joinMutation.mutate()
                      : () => navigate("/login")
                  }
                  isLoading={joinMutation.isLoading}
                >
                  Join the book club
                </Button>
              </Flex>
            ) : null}
          </Box>
        </Flex>
        {club.progress && (
          <Box position="relative">
            <Text
              position="absolute"
              right="3"
              bottom="1"
              zIndex="1"
              p="1"
              color="red.300"
              fontSize="xs"
              fontWeight="bold"
            >
              {club.progress.percentage}%
            </Text>
            <Progress
              value={club.progress.percentage}
              colorScheme="red"
              size="sm"
              height="4px"
              borderRadius="0"
            />
          </Box>
        )}
      </Box>

      {club.status === "upcoming" ? (
        <Box mt="6">
          <BookClubCountdown
            startsAt={club.startsAt}
            onComplete={() =>
              queryClient.invalidateQueries("communityBookClub")
            }
          />
        </Box>
      ) : (
        <Tabs mt="6" colorScheme="red" isLazy>
          <TabList>
            <Tab>Reading</Tab>
            <Tab>Discussions</Tab>
            <Tab>Members ({club.memberCount})</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px="0" pt="6">
              <BookClubReading club={club} />
            </TabPanel>
            <TabPanel px="0" pt="6">
              <Box>
                <Text fontSize="sm" color="gray.400" mb="3">
                  DISCUSSIONS
                </Text>
                {club.threads?.length ? (
                  club.threads.map((thread) => (
                    <Box
                      key={thread.id}
                      bg="gray.700"
                      borderRadius="lg"
                      p={{ base: "4", md: "5" }}
                      mb="4"
                    >
                      <Flex align="center" gap="3" mb="4">
                        <Avatar
                          size="sm"
                          src={thread.author?.profilePicture}
                          name={
                            thread.author?.userHandle || "Redwing community"
                          }
                        />
                        <Box flex="1" minW="0">
                          <Flex align="center" gap="2" wrap="wrap">
                            {thread.author?.userHandle ? (
                              <Link to={`/profile/${thread.author.userHandle}`}>
                                <Text
                                  fontSize="sm"
                                  fontWeight="bold"
                                  _hover={{ textDecoration: "underline" }}
                                >
                                  {thread.author.userHandle}
                                </Text>
                              </Link>
                            ) : (
                              <Text fontSize="sm" fontWeight="bold">
                                Redwing community
                              </Text>
                            )}
                            <Text
                              color="red.300"
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              OP
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              · {formatRelativeTime(thread.createdAt)}
                            </Text>
                          </Flex>
                        </Box>
                      </Flex>
                      <Heading size="md" mb="3">
                        {thread.title}
                      </Heading>
                      {thread.locked ? (
                        <Text color="orange.300">🔒 {thread.lockedNotice}</Text>
                      ) : (
                        <Text color="gray.300" whiteSpace="pre-wrap">
                          {thread.body}
                        </Text>
                      )}
                      {!thread.locked && (
                        <Box mt="5" pt="2">
                          {authData ? (
                            club.status === "active" ? (
                              <Flex gap="2">
                                <AutoGrowTextarea
                                  size="sm"
                                  value={
                                    commentDrafts[`thread-${thread.id}`] || ""
                                  }
                                  onChange={(event) =>
                                    setCommentDrafts((current) => ({
                                      ...current,
                                      [`thread-${thread.id}`]:
                                        event.target.value,
                                    }))
                                  }
                                  placeholder="Add to the conversation"
                                />
                                <Button
                                  alignSelf="end"
                                  colorScheme="red"
                                  onClick={() =>
                                    commentMutation.mutate({
                                      threadId: thread.id,
                                      body: commentDrafts[
                                        `thread-${thread.id}`
                                      ],
                                      draftKey: `thread-${thread.id}`,
                                    })
                                  }
                                  isLoading={commentMutation.isLoading}
                                  isDisabled={
                                    !commentDrafts[
                                      `thread-${thread.id}`
                                    ]?.trim()
                                  }
                                >
                                  Post
                                </Button>
                              </Flex>
                            ) : (
                              <Text color="gray.500" fontSize="sm">
                                Comments will open when the book club starts.
                              </Text>
                            )
                          ) : (
                            <Flex gap="2" align="center">
                              <Text color="gray.400" fontSize="sm">
                                Sign in to join the conversation.
                              </Text>
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                onClick={() => navigate("/login")}
                              >
                                Sign in
                              </Button>
                            </Flex>
                          )}
                          {thread.commentCount > 0 && (
                            <Box
                              mt="5"
                              borderTopWidth="1px"
                              borderColor="gray.600"
                              pt="4"
                            >
                              <Text fontSize="sm" color="gray.400" mb="3">
                                COMMENTS · {thread.commentCount}
                              </Text>
                              {thread.comments?.map((item) => (
                                <CommentItem
                                  key={item.id}
                                  item={{ ...item, threadId: thread.id }}
                                  depth={0}
                                  authData={authData}
                                  clubStatus={club.status}
                                  commentDrafts={commentDrafts}
                                  setCommentDrafts={setCommentDrafts}
                                  replyOpen={replyOpen}
                                  setReplyOpen={setReplyOpen}
                                  commentMutation={commentMutation}
                                  voteMutation={voteMutation}
                                  navigate={navigate}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  <Box bg="gray.700" borderRadius="lg" p="6">
                    <Text color="gray.400">
                      The first question will land here.
                    </Text>
                  </Box>
                )}
              </Box>
            </TabPanel>
            <TabPanel px="0" pt="6">
              <Box bg="gray.700" borderRadius="lg" p="6">
                <Text fontSize="sm" color="gray.400">
                  MEMBERS
                </Text>
                {club.members?.length ? (
                  club.members.map((member) => (
                    <Flex key={member.user.id} mt="4" align="center">
                      <Avatar
                        size="sm"
                        src={member.user.profilePicture}
                        name={member.user.userHandle}
                      />
                      <Text ml="3" flex="1">
                        {member.user.userHandle}
                      </Text>
                      <Text color="red.300">{member.percentage}%</Text>
                    </Flex>
                  ))
                ) : (
                  <Text mt="4" color="gray.400">
                    Join the club to see member progress.
                  </Text>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      <AlertDialog
        isOpen={isLeaveOpen}
        leastDestructiveRef={leaveCancelRef}
        onClose={() => {
          if (!leaveMutation.isLoading) {
            setIsLeaveOpen(false);
          }
        }}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.700">
          <AlertDialogContent bg="gray.700" mx="4">
            <AlertDialogHeader>Leave the Book Club?</AlertDialogHeader>
            <AlertDialogBody color="gray.300">
              Your comments will remain and{" "}
              {club.readingOrder?.title || "this reading order"} will become a
              normal reading again.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={leaveCancelRef}
                onClick={() => setIsLeaveOpen(false)}
                isDisabled={leaveMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml="3"
                onClick={() => leaveMutation.mutate()}
                isLoading={leaveMutation.isLoading}
                loadingText="Leaving"
              >
                Leave Book Club
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default BookClub;
